import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useEffect, useMemo, useState } from 'react'

import { usePrevious } from '@sequencehq/utils'

import {
  QUOTE_SEMANTIC_TYPES,
  useAutomationSimulationContext,
  type TerminalType
} from 'Automations/simulation'

type UseQuoteApproval = () => {
  isApprovalRequired: boolean
  isApprovalPending: boolean
  runningExecutionId: string | undefined
  approvalRequestedAt: string | undefined
  /**
   * Where the approval workflow is going to end up for this quote. Undefined
   * whenever we couldn't work that out,
   *
   * This is only updated after we have saved the quote, so if the user has the
   * quickest fingies in the west, then they will be waiting on the publish modal
   * looking at the spinner until we resolve this.
   */
  simulatedTerminalType: TerminalType | undefined
  isSimulationPending: boolean
  /**
   * The simulation has come back at least once.
   */
  simulationHasRanAtLeastOnce: boolean
}

export const useQuoteApproval: UseQuoteApproval = () => {
  const ctx = useCubeContext()
  const simulation = useAutomationSimulationContext()

  /**
   * The last answer the simulation gave us. It reports `undefined` for the
   * duration of a run, so holding the previous one here is what stops the
   * publish button blinking every time the quote is saved.
   */
  const [lastResult, setLastResult] = useState(simulation.result)

  useEffect(() => {
    if (simulation.result) setLastResult(simulation.result)
  }, [simulation.result])

  const simulatedTerminalType = useMemo(
    () =>
      lastResult?.outcome === 'RESOLVED' ? lastResult.terminalType : undefined,
    [lastResult]
  )

  /**
   * We know this quote needs a human when the simulation says so. Whenever it
   * can't say - no workflow, a rule we couldn't read, data we couldn't fetch -
   * we're back to "there's an approval workflow live, so assume it applies",
   * which is what this did before any of the simulating existed.
   */
  const isApprovalRequired = useMemo(() => {
    if (simulatedTerminalType) {
      return simulatedTerminalType === 'REVIEW'
    }

    return !!ctx.queries.rawData.data.quote
      .activeQuoteApprovalAutomationVersionId
  }, [
    simulatedTerminalType,
    ctx.queries.rawData.data.quote.activeQuoteApprovalAutomationVersionId
  ])

  const isApprovalPending = useMemo(() => {
    return !!ctx.queries.rawData.data.quote
      .runningQuoteApprovalAutomationExecutionId
  }, [ctx.queries.rawData.data.quote.runningQuoteApprovalAutomationExecutionId])

  useRefetchApprovalDataOnceTheRunEnds(isApprovalPending)

  const runningExecutionId = useMemo(() => {
    return ctx.queries.rawData.data.quote
      .runningQuoteApprovalAutomationExecutionId
  }, [ctx.queries.rawData.data.quote.runningQuoteApprovalAutomationExecutionId])

  const approvalRequestedAt = useMemo(() => {
    return ctx.queries.rawData.data.quote
      .runningQuoteApprovalAutomationExecutionCreateTime
  }, [
    ctx.queries.rawData.data.quote
      .runningQuoteApprovalAutomationExecutionCreateTime
  ])

  return {
    isApprovalRequired,
    isApprovalPending,
    runningExecutionId,
    approvalRequestedAt,
    simulatedTerminalType,
    isSimulationPending: simulation.isPending,
    simulationHasRanAtLeastOnce: Boolean(lastResult)
  }
}

/**
 * Goes back for the quote's approval data the moment a run it was waiting on
 * ends, whichever way it ended.
 *
 * The run that just finished is the quote's new previous approval, so every
 * rule comparing against one is answering about the approval before it until we
 * ask again. Nothing else prompts us to - the quote itself hasn't been edited.
 */
const useRefetchApprovalDataOnceTheRunEnds = (isApprovalPending: boolean) => {
  const { refetchDependencies } = useAutomationSimulationContext()
  const wasApprovalPending = usePrevious(isApprovalPending)

  const hasJustEnded = Boolean(wasApprovalPending) && !isApprovalPending

  useEffect(() => {
    if (!hasJustEnded) return

    void refetchDependencies(QUOTE_SEMANTIC_TYPES)
  }, [hasJustEnded, refetchDependencies])
}
